import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, getTeamMembers, getTeamName, getTeamActiveRepo } from '../firebase';
import { checkIfAdmin } from '../auth';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const AuthContext = createContext();
let cachedToken = null;
let cachedExpiry = 0;  // // timestamp in ms


export function useAuth() {
    return useContext(AuthContext);
}

const setGroupInfoToStorage = (groupInfo) => {
    localStorage.setItem('groupInfo', JSON.stringify(groupInfo));
};
const getGroupInfoFromStorage = () => {
    const groupInfo = localStorage.getItem('groupInfo');
    return groupInfo ? JSON.parse(groupInfo) : null;
};
const removeGroupInfoFromStorage = () => localStorage.removeItem('groupInfo');

const setCourseInfoToStorage = (courseInfo) => {
    localStorage.setItem('courseInfo', JSON.stringify(courseInfo));
};
const getCourseInfoFromStorage = () => {
    const courseInfo = localStorage.getItem('courseInfo');
    return courseInfo ? JSON.parse(courseInfo) : null;
};
const removeCourseInfoFromStorage = () => localStorage.removeItem('courseInfo');

const saveGoogleAccessToken = (token) => {
    cachedToken = token;
    cachedExpiry = Date.now() + 3500 * 1000; // ~1 hour
    localStorage.setItem("g_access_token", token);
    localStorage.setItem("g_access_token_expiry", cachedExpiry.toString());
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [groupMembers, setGroupMembers] = useState([]);
    const [groupMembersLoading, setGroupMembersLoading] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [isView, setIsView] = useState(false);
    const [groupActiveRepo, setGroupActiveRepo] = useState(null);
    const [courseInfo, setCourseInfoState] = useState(
        getCourseInfoFromStorage() || {}
    );
    const courseId = courseInfo?.courseId;


    const setCourseInfo = useCallback((updater) => {
        setCourseInfoState(prev => {
            const safePrev = prev || {}; 
    
            const newValue =
                typeof updater === "function"
                    ? updater(safePrev)
                    : updater;
    
            setCourseInfoToStorage(newValue);
            return newValue;
        });
    }, []);

    const fetchGroupData = useCallback(async (authUser, currentGroupId) => {
        if (currentGroupId) {
            setGroupMembersLoading(true);
            try {
                //console.log("Fetching group data for groupId:", currentGroupId);
                const membersData = await getTeamMembers(authUser, currentGroupId);
                //console.log("Fetched members data:", membersData);
                setGroupMembers(membersData.members || []);

                const name = await getTeamName(authUser, currentGroupId);
                //console.log("Fetched group name:", name);
                setGroupName(name || 'Unnamed Group');
                const ActiveRepo = await getTeamActiveRepo(currentGroupId);
                //console.log("Fetched Active Repo info:", ActiveRepo);
                setGroupActiveRepo(ActiveRepo);
            } catch (error) {
                //console.error("Error fetching group data:", error);
                setGroupMembers([]);
                setGroupName("");
                setGroupActiveRepo(null)
            } finally {
                setGroupMembersLoading(false);
            }
        }
    }, []);

    // Ensures user has valid Google Drive + Docs scopes.
    // If missing or expired (401) → triggers Google OAuth popup and returns a new token.
    const ensureGoogleDriveAccess = async (user, runGoogleOAuth) => {
        if (!user) throw new Error("User not logged in");

        // Get current access token
        let token = await getGoogleAccessToken(user);

        // Case 1 — No token (email/password login or revoked access)
        if (!token) {
            console.log("No Google Drive token → triggering OAuth popup...");
            const newToken = await runGoogleOAuth();
            return newToken;
        }

        // Case 2 — Token exists → validate with a lightweight request
        const testRes = await fetch(
            "https://www.googleapis.com/drive/v3/about?fields=user",
            { headers: { Authorization: `Bearer ${token}` } }
        );

        // Token still valid → use it
        if (testRes.status !== 401) return token;

        // Case 3 — Token revoked or expired → trigger OAuth once
        console.warn("Drive token invalid (401) → re-authenticating...");
        const refreshed = await runGoogleOAuth();
        return refreshed;
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
            setLoading(true);            
            if (authUser) {
                try {
                    const adminStatus = await checkIfAdmin(authUser.email);
                    setIsAdmin(adminStatus);
                    setUser(authUser);
                    const storedGroupInfo = getGroupInfoFromStorage();
                    const storedGroupId = storedGroupInfo ? storedGroupInfo.groupId : null;
                    if (storedGroupId) {
                        await fetchGroupData(authUser, storedGroupId);
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                    setIsAdmin(false);
                    setGroupMembers([]);
                    setGroupName("");
                }
            } else {
                setUser(null);
                setIsAdmin(false);
                setGroupMembers([]);
                setGroupName("");
                removeGroupInfoFromStorage();
                removeCourseInfoFromStorage();
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fetchGroupData]);

    useEffect(() => {
        if (!courseId) return;
    
        const ref = doc(db, "Courses", courseId);
    
        const unsubscribe = onSnapshot(ref, (snap) => {
            if (!snap.exists()) return;
    
            const data = snap.data();
    
            setCourseInfo(prev => ({
                ...(prev || {}),
                courseId: snap.id,
                name: data.name,
                startDate: data.startDate,
                endDate: data.endDate,
                artifacts: data.settings?.artifacts,
                deadlineEnabled: data.settings?.deadlineEnabled ?? true,
            }));
        });
    
        return () => unsubscribe();
    }, [courseId, setCourseInfo]);

    const setGroupInfoAndFetchData = useCallback(async (newGroupInfo) => {
        setGroupInfoToStorage(newGroupInfo);
        const newGroupId = newGroupInfo.groupId;
        if (user) {
            await fetchGroupData(user, newGroupId);
        }
    }, [user, fetchGroupData]);

    const getGoogleAccessToken = async () => {
        if (cachedToken && Date.now() < cachedExpiry) {
            return cachedToken;
        }

        const stored = localStorage.getItem("g_access_token");
        const expiry = parseInt(localStorage.getItem("g_access_token_expiry") || "0", 10);
        if (stored && Date.now() < expiry) return stored;

        return null;
    };


    const value = {
        user,
        isView,
        setIsView,
        setUser,
        loading,
        groupMembers,
        groupName,
        isAdmin,
        groupInfo: getGroupInfoFromStorage(),
        setGroupInfo: setGroupInfoAndFetchData,
        courseInfo,
        setCourseInfo,
        groupMembersLoading,
        groupActiveRepo,
        setGroupActiveRepo,
        setGroupMembers,
        getGoogleAccessToken,
        saveGoogleAccessToken,
        ensureGoogleDriveAccess
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && (!isAdmin || !groupMembersLoading) && children}
        </AuthContext.Provider>
    );
}
